import React, {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import { createPortal } from "react-dom";

import { ApiError } from "@/src/api";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useForm } from "react-hook-form";

import { t } from "i18next";

import { Box, Typography } from "@mui/material";

import { notificationAtom } from "@/src/state/notificationAtom";

import UploadProgressIndicator from "@/src/components/molecules/UploadProgressIndicator";

import { useAuth } from "@/src/auth/hooks/useAuth";
import {
  FeedbackRequest,
  FeedbackResponse
} from "@/src/queries/feedback/feedbackFunctions";
import {
  usePostFeedback,
  useUploadFeedbackImage
} from "@/src/queries/feedback/feedbackQueries";

import FeedbackAttachmentSection from "./FeedbackAttachmentSection";
import FeedbackFormActions from "./FeedbackFormActions";
import FeedbackFormFields from "./FeedbackFormFields";
import {
  FeedbackFormValues,
  createFeedbackFormValidator
} from "./feedbackFormValidator";
import { type FeedbackIssueTypeSlug } from "./feedbackSubjectOptions";
import FeedbackSuccessState from "./FeedbackSuccessState";
import { buildFeedbackDefaultValues, getErrorMessage } from "./feedbackUtils";
import { useFeedbackAttachments } from "./useFeedbackAttachments";

import { useSetAtom } from "jotai";

interface FeedbackFormProps {
  userType: "consumer" | "dealer";
  /** When true, hides the form title (e.g. when parent renders it in a shared header) */
  hideTitle?: boolean;
}

const FeedbackForm = ({ userType, hideTitle }: FeedbackFormProps) => {
  const { user, isAuthenticated } = useAuth();
  const setNotification = useSetAtom(notificationAtom);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [submissionState, setSubmissionState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [hasImageFailures, setHasImageFailures] = useState(false);

  const { mutate: submitFeedback } = usePostFeedback();
  const { mutateAsync: uploadImage } = useUploadFeedbackImage();

  const {
    attachments,
    previewUrls,
    lightboxIndex,
    setLightboxIndex,
    handleFileSelect,
    handleRemoveAttachment,
    resetAttachments
  } = useFeedbackAttachments();

  const validationSchema = useMemo(() => createFeedbackFormValidator(), []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<FeedbackFormValues>({
    defaultValues: buildFeedbackDefaultValues(user),
    resolver: yupResolver(
      validationSchema as yup.ObjectSchema<FeedbackFormValues>
    ),
    mode: "onChange"
  });

  useLayoutEffect(() => {
    if (submissionState !== "success") {
      return;
    }
    if (globalThis.window === undefined) {
      return;
    }
    globalThis.window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [submissionState]);

  const resetForm = () => {
    reset(buildFeedbackDefaultValues(user));
    resetAttachments();
  };

  const handleNewFeedback = () => {
    setSubmissionState("idle");
    setHasImageFailures(false);
    resetForm();
    setIsFormDisabled(false);
  };

  const handleUploadImages = useCallback(
    async (caseId: string): Promise<boolean> => {
      setUploadProgress({ current: 0, total: attachments.length });

      for (let i = 0; i < attachments.length; i++) {
        setUploadProgress({ current: i + 1, total: attachments.length });
        try {
          await uploadImage({ caseId, file: attachments[i], isAuthenticated });
        } catch {
          setUploadProgress(null);
          setNotification({
            text: t("feedbackForm.error.imageUploadFailed", {
              filename: attachments[i].name
            }),
            variant: "error"
          });
          return true;
        }
      }

      setUploadProgress(null);
      return false;
    },
    [attachments, isAuthenticated, setNotification, uploadImage]
  );

  const handleMutationSuccess = useCallback(
    async (data: FeedbackResponse) => {
      if (attachments.length > 0) {
        const failed = await handleUploadImages(data.caseId);
        setHasImageFailures(failed);
      }

      setIsFormDisabled(false);
      setSubmissionState("success");
    },
    [attachments.length, handleUploadImages]
  );

  const handleMutationError = useCallback(
    (error: Error) => {
      const apiError = error as ApiError<{ message: string }>;
      const status = apiError?.status ?? 500;

      setNotification({
        text: getErrorMessage(status),
        variant: "error",
        ...(status === 429 && { isAutoHideDisabled: true })
      });

      setSubmissionState("error");
      setIsFormDisabled(false);
    },
    [setNotification]
  );

  const onSubmit = (values: FeedbackFormValues) => {
    setSubmissionState("submitting");
    setIsFormDisabled(true);

    const payload: FeedbackRequest = {
      site: "Baana",
      postedBy: userType === "dealer" ? "dealer" : "seller",
      email: values.email,
      message: values.message,
      issueType: values.issueType as FeedbackIssueTypeSlug,
      ...(values.name && { name: values.name }),
      ...(values.mobile && { mobile: values.mobile })
    };

    submitFeedback(
      { data: payload, isAuthenticated },
      {
        onSuccess: handleMutationSuccess,
        onError: handleMutationError
      }
    );
  };

  const getProgressText = (): string | null => {
    if (!isFormDisabled) return null;
    if (uploadProgress) {
      return t("feedbackForm.progress.uploadingImage", {
        current: uploadProgress.current,
        total: uploadProgress.total
      });
    }
    return t("feedbackForm.progress.sending");
  };

  const showUploadProgress =
    submissionState !== "success" &&
    ((isFormDisabled && attachments.length > 0) || uploadProgress !== null);

  let progressIndicator: ReactNode = null;
  if (showUploadProgress) {
    const progressStatusText = getProgressText() ?? "";
    progressIndicator = (
      <UploadProgressIndicator
        uploaded={uploadProgress?.current ?? 0}
        total={uploadProgress?.total ?? attachments.length}
        isVisible={true}
        statusText={progressStatusText}
        warningText={t("feedbackForm.progress.doNotRefreshWarning") as string}
      />
    );
  }

  let mainSection: ReactNode;
  if (submissionState === "success") {
    mainSection = (
      <FeedbackSuccessState
        hasImageFailures={hasImageFailures}
        onNewFeedback={handleNewFeedback}
      />
    );
  } else {
    mainSection = (
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          <FeedbackFormFields
            control={control}
            errors={errors}
            isFormDisabled={isFormDisabled}
            userType={userType}
          />

          <FeedbackAttachmentSection
            attachments={attachments}
            previewUrls={previewUrls}
            lightboxIndex={lightboxIndex}
            isFormDisabled={isFormDisabled}
            onFileSelect={handleFileSelect}
            onRemove={handleRemoveAttachment}
            onThumbnailClick={setLightboxIndex}
            onLightboxClose={() => setLightboxIndex(null)}
            onLightboxIndexChange={setLightboxIndex}
          />

          <FeedbackFormActions
            isFormDisabled={isFormDisabled}
            isValid={isValid}
            progressText={getProgressText()}
            onReset={resetForm}
          />
        </Box>
      </Box>
    );
  }

  return (
    <>
      {typeof document !== "undefined" &&
        createPortal(progressIndicator, document.body)}

      <Box sx={{ maxWidth: 520 }}>
        {!hideTitle && (
          <Typography variant="h2" sx={{ mb: 3 }}>
            {t("feedbackForm.title")}
          </Typography>
        )}

        {mainSection}
      </Box>
    </>
  );
};

export default FeedbackForm;
